import * as React from "react"
import { useRouteMatch } from "react-router-dom"
import { ReportKind } from "@digits-graphql/frontend/graphql-bearer"
import { SvgBank } from "@digits-shared/components/SVGIcons/line/Bank.svg"
import { SvgBookOpen01 } from "@digits-shared/components/SVGIcons/line/BookOpen01.svg"
import { SvgBuilding02 } from "@digits-shared/components/SVGIcons/line/Building02.svg"
import { SvgCornerDownLeft } from "@digits-shared/components/SVGIcons/line/CornerDownLeft.svg"
import { SvgCreditCardAngle } from "@digits-shared/components/SVGIcons/line/CreditCardAngle.svg"
import { SvgFile06 } from "@digits-shared/components/SVGIcons/line/File06.svg"
import { SvgFile07 } from "@digits-shared/components/SVGIcons/line/File07.svg"
import { SvgFileLock03 } from "@digits-shared/components/SVGIcons/line/FileLock03.svg"
import { SvgGrid01 } from "@digits-shared/components/SVGIcons/line/Grid01.svg"
import { SvgHome02 } from "@digits-shared/components/SVGIcons/line/Home02.svg"
import { SvgInvoicing } from "@digits-shared/components/SVGIcons/line/Invoicing.svg"
import { SvgMoneyBag } from "@digits-shared/components/SVGIcons/line/MoneyBag.svg"
import { SvgScales02 } from "@digits-shared/components/SVGIcons/line/Scales02.svg"
import { SvgSettings01 } from "@digits-shared/components/SVGIcons/line/Settings01.svg"
import { SvgTags } from "@digits-shared/components/SVGIcons/line/Tags.svg"
import { SvgUser01 } from "@digits-shared/components/SVGIcons/line/User01.svg"
import useSession from "@digits-shared/hooks/useSession"
import { AspectCode } from "@digits-shared/session/AspectCode"
import styled from "styled-components"
import { AccountingSidebar } from "src/frontend/components/Shared/NavSidebar/AccountingSidebar"
import {
  ActionItemsSidebarRow,
  InboxIcon,
} from "src/frontend/components/Shared/NavSidebar/ActionItemsSidebarRow"
import { ConnectionsSidebar } from "src/frontend/components/Shared/NavSidebar/ConnectionsSidebar"
import {
  SIDEBAR_PATH_SVG_STYLES,
  SidebarDivider,
} from "src/frontend/components/Shared/NavSidebar/sidebarConstants"
import { SidebarRow } from "src/frontend/components/Shared/NavSidebar/SidebarExpando"
import { useNavSidebarContext } from "src/frontend/components/Shared/NavSidebar/useNavSidebarContext"
import { RequiresUpdate } from "src/frontend/components/Shared/Permissions/Requires"
import { RequiresAspect } from "src/frontend/components/Shared/Permissions/RequiresAspect"
import { RequiresView } from "src/frontend/components/Shared/View/RequiresView"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session/FrontendSession"
import {
  FrontendPermissionModule,
  FrontendPermissionSource,
} from "src/frontend/session/permissionModule"

/*
  STYLES
*/

const Contents = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
`

const HomeIcon = styled(SvgHome02)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const GetPaidIcon = styled(SvgMoneyBag)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const BillPayIcon = styled(SvgCreditCardAngle)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const InvoicingIcon = styled(SvgInvoicing)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const FinancialsIcon = styled(SvgFile06)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const ReportsIcon = styled(SvgFile07)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const LedgerIcon = styled(SvgBookOpen01)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const VaultIcon = styled(SvgFileLock03)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const BackIcon = styled(SvgCornerDownLeft)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const VendorsIcon = styled(SvgBuilding02)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const CustomersIcon = styled(SvgUser01)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const CategoryManagerIcon = styled(SvgTags)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const BanksAndCardsIcon = styled(SvgBank)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const OnboardingIcon = styled(SvgGrid01)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const AllClientsIcon = styled(SvgGrid01)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const SettingsIcon = styled(SvgSettings01)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const ReconciliationsIcon = styled(SvgScales02)`
  ${SIDEBAR_PATH_SVG_STYLES};
`

const MutedSidebarRow = styled(SidebarRow)`
  opacity: 0.2;
`

/*
  COMPONENT
*/

export const SidebarContents: React.FC = () => {
  const { currentLegalEntity, hasAccessToAspect, currentOrganization } =
    useSession<FrontendSession>()
  const params = { leSlug: currentLegalEntity?.slug }
  const financialsParams = { ...params, kind: ReportKind.ProfitAndLoss }

  const { showBillPay, showInvoicing, showGetPaid, showAccounting, showAudit } = {
    showBillPay: hasAccessToAspect(AspectCode.PayBills),
    showInvoicing: hasAccessToAspect(AspectCode.Invoicing),
    showGetPaid: hasAccessToAspect(AspectCode.GetPaid),
    showAccounting: hasAccessToAspect(AspectCode.AIBookkeeper),
    showAudit: hasAccessToAspect(AspectCode.Audit),
  }

  const { isHeaderMenuOpen } = useNavSidebarContext()

  const isOnFirmDashboard = useRouteMatch(routes.firmDashboardRoot.parameterizedPath)

  if (isOnFirmDashboard) {
    if (!currentOrganization?.slug) return <Contents inert />

    return (
      <Contents inert={isHeaderMenuOpen}>
        <SidebarRow
          Icon={AllClientsIcon}
          route={routes.firmDashboardFirmKnown}
          activeRoutes={[routes.firmDashboardFirmKnown]}
          params={{ orgSlug: currentOrganization.slug }}
          isExact
        >
          All Clients
        </SidebarRow>
        <RequiresUpdate
          source={FrontendPermissionSource.Organization}
          module={FrontendPermissionModule.Organizations}
        >
          <SidebarRow
            Icon={SettingsIcon}
            route={routes.firmSettings}
            params={{ orgSlug: currentOrganization?.slug ?? "" }}
          >
            Firm Settings
          </SidebarRow>
        </RequiresUpdate>
      </Contents>
    )
  }

  return (
    <Contents inert={isHeaderMenuOpen}>
      <RequiresView>
        <SidebarRow
          Icon={HomeIcon}
          route={routes.legalEntityHome}
          activeRoutes={[routes.legalEntityHome]}
          params={params}
        >
          Home
        </SidebarRow>
        <ActionItemsSidebarRow params={params} />

        <SidebarDivider />
      </RequiresView>

      {(showBillPay || showInvoicing || showGetPaid || showAccounting) && (
        <>
          {showAccounting && (
            <RequiresView>
              <SidebarRow Icon={BanksAndCardsIcon} route={routes.banksAndCards} params={params}>
                Banks & Cards
              </SidebarRow>
            </RequiresView>
          )}
          {showBillPay && (
            <SidebarRow Icon={BillPayIcon} route={routes.billPay} params={params}>
              Bills
            </SidebarRow>
          )}
          {showInvoicing && (
            <SidebarRow Icon={InvoicingIcon} route={routes.invoicing} params={params}>
              Invoices
            </SidebarRow>
          )}
          {showGetPaid && (
            <SidebarRow Icon={GetPaidIcon} route={routes.getPaid} params={params} isExact>
              Get Paid
            </SidebarRow>
          )}
          <SidebarDivider />
        </>
      )}

      <RequiresView>
        {showAccounting && (
          <>
            <SidebarRow Icon={VendorsIcon} route={routes.vendorDirectory} params={params}>
              Vendors
            </SidebarRow>

            <SidebarRow Icon={CustomersIcon} route={routes.customerDirectory} params={params}>
              Customers
            </SidebarRow>

            <SidebarDivider />
          </>
        )}

        <SidebarRow Icon={FinancialsIcon} route={routes.financials} params={financialsParams}>
          Financials
        </SidebarRow>

        <SidebarRow Icon={ReportsIcon} route={routes.reports} params={params}>
          Custom Reports
        </SidebarRow>

        <SidebarDivider />

        {(showAccounting || showAudit) && <AccountingSidebar />}

        <RequiresAspect aspect={AspectCode.Vault}>
          <SidebarRow Icon={VaultIcon} route={routes.vault} params={params}>
            Documents
          </SidebarRow>
        </RequiresAspect>
      </RequiresView>

      <ConnectionsSidebar />
    </Contents>
  )
}

// It is filled via a Portal with contents from the Shared With Me entities
// See ObjectKindSelector.tsx
export const SharedUserSidebarContents: React.FC = () => {
  const { currentLegalEntity, isAffiliatedSession, currentOrganization, allActiveLegalEntities } =
    useSession<FrontendSession>()

  // Make sure that we can actually return to the dashboard for the given legal entity.
  const navigableLegalEntity = React.useMemo(() => {
    if (currentLegalEntity?.accountingViewVersions.size > 0) {
      return currentLegalEntity
    }

    const allDashboardLegalEntities = isAffiliatedSession
      ? currentOrganization?.affiliatedLegalEntities.filter((le) => le.hasDashboardAccess())
      : allActiveLegalEntities.map((le) => le.legalEntity)

    const navigableLEs = (allDashboardLegalEntities ?? [])
      .filter((le) => le.accountingViewVersions.size > 0)
      .sort((a, b) => (a.slug < b.slug ? -1 : 1))
    return navigableLEs.length ? navigableLEs[0] : undefined
  }, [
    allActiveLegalEntities,
    currentLegalEntity,
    currentOrganization?.affiliatedLegalEntities,
    isAffiliatedSession,
  ])

  const params = { leSlug: navigableLegalEntity?.slug ?? "" }
  const hasLEAccess = !!navigableLegalEntity

  return (
    <Contents id="nav-sidebar-contents">
      {hasLEAccess && (
        <SidebarRow Icon={BackIcon} route={routes.legalEntityHome} params={params} isExact>
          Back to Home
        </SidebarRow>
      )}
    </Contents>
  )
}

export const OnboardingSidebarContents: React.FC = () => {
  const { isHeaderMenuOpen } = useNavSidebarContext()

  return (
    <Contents inert={isHeaderMenuOpen}>
      <SidebarRow Icon={OnboardingIcon} rowType="button" active>
        Onboarding
      </SidebarRow>
      <MutedSidebarRow Icon={HomeIcon}>Home</MutedSidebarRow>
      <MutedSidebarRow Icon={InboxIcon}>Inbox</MutedSidebarRow>
      <SidebarDivider />
      <MutedSidebarRow Icon={BanksAndCardsIcon}>Banks & Cards</MutedSidebarRow>
      <MutedSidebarRow Icon={BillPayIcon}>Bills</MutedSidebarRow>
      <MutedSidebarRow Icon={InvoicingIcon}>Invoices</MutedSidebarRow>
      <SidebarDivider />
      <MutedSidebarRow Icon={VendorsIcon}>Vendors</MutedSidebarRow>
      <MutedSidebarRow Icon={CustomersIcon}>Customers</MutedSidebarRow>
      <SidebarDivider />
      <MutedSidebarRow Icon={FinancialsIcon}>Financials</MutedSidebarRow>
      <MutedSidebarRow Icon={ReportsIcon}>Custom Reports</MutedSidebarRow>
      <SidebarDivider />
      <MutedSidebarRow Icon={LedgerIcon}>Ledger</MutedSidebarRow>
      <MutedSidebarRow Icon={ReconciliationsIcon}>Reconciliations</MutedSidebarRow>
      <MutedSidebarRow Icon={CategoryManagerIcon}>Category Manager</MutedSidebarRow>
    </Contents>
  )
}
